import {
  createColumnHelper,
  getCoreRowModel,
  type PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { DownloadIcon, FileIcon } from "lucide-react";
import { usePaginatedMarketingResources, webDownload } from "creso-core/hooks";
import type { MarketingResource } from "creso-core/types";
import {
  Button,
  DataTable,
  DataTablePagination,
  SearchInput,
  ScrollArea,
  ScrollBar,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  V2Loader,
} from "web-design-system";
import { Link } from "@tanstack/react-router";
import { useDebounceValue } from "usehooks-ts";
import {
  formatResourceDate,
  getNewsletterPages,
  getResourceLink,
} from "creso-core/utils";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const columnHelper = createColumnHelper<MarketingResource>();

export function NewsletterList() {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {
    data: resourcesResponse,
    isPending,
    isFetching,
  } = usePaginatedMarketingResources({
    resource_type: "newsletter",
    search: debouncedSearch || undefined,
    page: pagination.pageIndex + 1,
    page_size: pagination.pageSize,
    month: selectedMonth === "all" ? undefined : Number(selectedMonth),
    year: selectedYear === "all" ? undefined : Number(selectedYear),
  });

  const isLoadingResources = isPending || isFetching;

  const resources = useMemo(
    () => resourcesResponse?.results ?? [],
    [resourcesResponse]
  );
  const totalCount = resourcesResponse?.count ?? 0;

  const monthOptions = useMemo(() => {
    const months = resourcesResponse?.months ?? [];

    return [
      { value: "all", label: "All Months" },
      ...months.map((month) => ({
        value: String(month),
        label: MONTH_LABELS[month - 1] ?? String(month),
      })),
    ];
  }, [resourcesResponse?.months]);

  const yearOptions = useMemo(() => {
    const years = resourcesResponse?.years ?? [];

    return ["all", ...years.map(String)];
  }, [resourcesResponse?.years]);

  useEffect(() => {
    const availableMonths = resourcesResponse?.months ?? [];
    if (
      selectedMonth !== "all" &&
      !availableMonths.includes(Number(selectedMonth))
    ) {
      setSelectedMonth("all");
    }
  }, [resourcesResponse?.months, selectedMonth]);

  useEffect(() => {
    const availableYears = resourcesResponse?.years ?? [];
    if (
      selectedYear !== "all" &&
      !availableYears.includes(Number(selectedYear))
    ) {
      setSelectedYear("all");
    }
  }, [resourcesResponse?.years, selectedYear]);

  const handleDownload = async (resource: MarketingResource) => {
    const url = getResourceLink(resource);

    if (!url) {
      return;
    }

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const pdfBlob = await response.blob();
      await webDownload(pdfBlob, `${resource.title}.pdf`);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("title", {
        header: "Title",
        size: 520,
        cell: ({ row }) => (
          <Link
            to={getResourceLink(row.original) ?? ""}
            target="_blank"
            className="no-underline!"
          >
            <div className="flex items-center gap-2">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-tag-yellow/80 text-tag-yellow-foreground">
                <FileIcon className="size-4" />
              </div>
              <div className="line-clamp-1 text-sm">{row.original.title}</div>
            </div>
          </Link>
        ),
      }),
      columnHelper.display({
        id: "pages",
        header: "Pages",
        size: 140,
        cell: ({ row }) => (
          <div className="text-foreground text-sm">
            {getNewsletterPages(row.original)}
          </div>
        ),
      }),
      columnHelper.display({
        id: "published",
        header: "Published",
        size: 160,
        cell: ({ row }) => (
          <div className="text-foreground text-sm">
            {formatResourceDate(row.original.publish_date ?? "", "do MMM yyyy")}
          </div>
        ),
      }),
      columnHelper.display({
        id: "action",
        header: () => <div className="text-center">Action</div>,
        size: 50,
        maxSize: 50,
        minSize: 50,
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Button
              variant="link"
              className="h-auto p-0 text-sm [&_svg]:size-5 cursor-pointer"
              onClick={() => handleDownload(row.original)}
            >
              <DownloadIcon className="size-5" />
            </Button>
          </div>
        ),
      }),
    ],
    []
  );

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [debouncedSearch, selectedMonth, selectedYear]);

  const rowCount = totalCount;

  const table = useReactTable({
    data: resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
    rowCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    enableRowSelection: false,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="flex gap-3 flex-row items-center justify-between p-1">
        <div className="w-full lg:max-w-sm">
          <SearchInput
            value={search}
            placeholder="Search by title / content"
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>

        <div className="flex gap-3 flex-row items-center">
          <Select
            value={selectedMonth}
            onValueChange={(value) => {
              setSelectedMonth(value);
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {monthOptions.map((month) => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedYear}
            onValueChange={(value) => {
              setSelectedYear(value);
            }}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year}>
                  {year === "all" ? "All Years" : year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <V2Loader
        isLoading={isLoadingResources}
        className="flex-1 overflow-hidden"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border/60">
          <ScrollArea className="flex flex-1 flex-col">
            <DataTable
              table={table}
              pagination={false}
              className="flex-1 border-none"
              emptyState={() => (
                <div className="text-muted-foreground pt-12 text-sm size-full top-0 absolute flex flex-col items-center justify-center">
                  No newsletters match the current search or date filters.
                </div>
              )}
            />
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          <DataTablePagination table={table} className="sticky bottom-0 z-20" />
        </div>
      </V2Loader>
    </div>
  );
}
